import { createApi } from '@reduxjs/toolkit/query/react';
import { DocumentDetails, FinancialInfo } from '../slices/assetCreateSlice';
import { createExtendedTokenBaseQuery } from './baseQuery';
import type { DocumentDetail } from '../../services/assetTransferService';

// Define Transfer interface
export interface Transfer {
  id: string;
  transfer_date: string;
  from_location: string;
  from_location_detail: {
    id: string;
    name: string;
  };
  to_location: string;
  to_location_detail: {
    id: string;
    name: string;
  };
  back_to_store: boolean;
  assigned_to: string;
  assigned_to_detail: {
    id: string;
    name: string;
    email?: string;
  };
  department: string;
  department_detail: {
    id: string;
    name: string;
  };
  transfer_cost: string;
  reason: string;
  status: string;
  workflow_instance: string | null;
  documents: Array<{
    id: string;
    file: string;
    description: string;
    uploaded_at: string;
    uploaded_by: {
      id: string;
      name: string;
    };
  }>;
  comments: string;
  created_at: string;
  created_by_detail: {
    id: string;
    name: string;
  };
}

// Define Asset interface
export interface NamedEntity {
  id: string;
  asset_name?: string;
  name?: string;
}

export interface User {
  id: string;
  name: string;
  email?: string;
}

export interface UserGroup {
  id: string;
  name: string;
  email?: string; 
}

export interface ScheduledActivity {
  id: string;
  activity_type: string;
  user_group: UserGroup;
  executed_by: UserGroup | null;
  activity_date: string;
}

export interface AuditTask {
  id: string;
  name: string;
  audit_type: string;
  audited_by: UserGroup | null;
  start_date: string;
  end_date: string;
  status: string;
}

export interface VendorDetail {
  id: string;
  name: string;
}

// Matches AssetWarrantySerializer
export interface Warranty {
  id: string;
  asset: string;
  warranty_from: string | null;
  warranty_to: string | null;
  warranty_vendor: string | null;
  warranty_vendor_detail: VendorDetail | null;
  amc_vendor: string | null;
  amc_vendor_detail: VendorDetail | null;
  insurance_start_date: string | null;
  insurance_end_date: string | null;
  amc_start_date: string | null;
  amc_end_date: string | null;
  created_at?: string;
  created_by?: string | null;
  document_ids?: string[];
  documents_detail?: DocumentDetail[];
}

interface LocationDetail {
  id: string;
  name: string;
  location_type?: string;
  location_hierarchy?: string; // Add this field to hold the hierarchy string
}

export interface Asset {
  id: string;
  asset_tag: string;
  asset_name: string;
  company?: string;
  description?: string;
  asset_category?: string;
  asset_category_detail?: NamedEntity;
  asset_status?: string;
  asset_status_detail?: NamedEntity;
  asset_condition?: string | null;
  asset_condition_detail?: NamedEntity | null;
  parent_asset?: string | null;
  parent_asset_detail?: NamedEntity | null;
  manufacturer?: string;
  manufacturer_detail?: NamedEntity;
  model?: string;
  model_detail?: NamedEntity;
  serial_number?: string;
  purchase_value?: string;
  current_value?: string | null;
  purchase_date?: string; // ISO date string
  location?: string;
  location_detail?: LocationDetail;
  // Populated by RFID warehouse-tracking scans, not user-entered.
  last_scanned_location?: string | null;
  last_scanned_location_detail?: LocationDetail | null;
  last_scanned_at?: string | null;
  department?: string;
  department_detail?: NamedEntity;
  assigned_to?: string | null;
  assigned_to_detail?: User | null;
  assigned_to_email?: string | null;
  transfer_history?: any[]; // could be typed separately if you have a schema
  tags?: Array<{
    id: string;
    name: string;
  }>;
  warranties_detail?: Warranty | null; // latest warranty only
  audit_requests?: AuditTask[];
  scheduled_activities?: ScheduledActivity[];
  total_expense?: number;
  far_reference?: string | null;
  eam_reference?: string | null;
  is_disposed?: boolean;
  custom_fields?: Record<string, any>;
  custom_fields_detail?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
  documents?: Array<{
    id: string;
    name: string;
    url: string;
    file_type?: string;
    created_at?: string;
  }>;
  images?: Array<{
    id: string;
    name: string;
    url: string;
    file_type?: string;
    created_at?: string;
  }>;
  images_detail?: Array<{
    id: string;
    asset: string;
    image_url: string;
    image_sas_url: string;
    is_primary: boolean;
    created_at: string;
    created_by: string;
  }>;
  financial_info_detail?: FinancialInfo;
  purchase_info_detail?: {
    vendor: string;
    invoice_date: string;
    invoice_number: number | string;
  }
}

export interface AssetCreateInput {
  asset_tag: string;
  asset_name: string;
  description?: string;
  asset_category?: string;
  asset_condition?: string;
  asset_status?: string;
  manufacturer?: string;
  model?: string;
  serial_number?: string;
  purchase_value?: string;
  current_value?: string;
  purchase_date?: string;
  location?: string;
  department?: string;
  assigned_to?: string;
  documents?: DocumentDetails[];
  image?: File | null;
  images?: File[];
}

interface GetAssetsParams {
  limit?: number;
  offset?: number;
  page?: number;
  search?: string;
  // Column search parameters
  col_asset_tag?: string;
  col_name?: string;
  col_category?: string;
  col_manufacturer?: string;
  col_model?: string;
  col_condition?: string;
  col_parent_asset?: string;
  col_location?: string;
  col_department?: string;
  // Allow any other params
  [key: string]: string | number | boolean | undefined;
}

export interface TagTemplateElementConfig {
  // text
  content?: string;
  font_name?: string;
  font_size?: number;
  align?: "left" | "center" | "right";
  color?: string;
  // image
  object_fit?: "contain" | "cover" | "fill";
  // barcode / qrcode (reserved for future driver options)
  [key: string]: unknown;
}

export interface TagTemplateElementDocument {
  id: string;
  filename: string;
  file_size: number;
  file_type: string;
}

export interface TagTemplateElement {
  id: string;
  element_type: "barcode" | "qrcode" | "text" | "image";
  x: number;
  y: number;
  width: number;
  height: number;
  z_index: number;
  config: TagTemplateElementConfig;
  // The uploaded image is a Document (see apps.documents), not a raw file
  // field. image_document is its id (write); image_document_detail is the
  // read-only nested summary the API returns — its actual bytes are fetched
  // separately via /documents/documents/{id}/inline/.
  image_document?: string | null;
  image_document_detail?: TagTemplateElementDocument | null;
}

export interface AssetTagTemplatePrinterSettings {
  batch_cut: number;
  cut_after_printing: boolean;
  print_speed: number;
  print_mirror: boolean;
  is_continuous: boolean;
  gap_length: number;
  mark_length: number;
  offset_length: number;
}

export interface AssetTagTemplate {
  id: string;
  name: string;
  description: string;
  unit: "mm" | "inch";
  orientation: "portrait" | "landscape";

  label_width: number;
  label_height: number;

  elements: TagTemplateElement[];
  // Server-derived from `elements`: "Barcode" | "QR Code" | "Barcode + QR" | "—".
  template_type_display?: string;

  is_default: boolean;
  is_active: boolean;

  // Appearance
  background_color: string;

  // Sheet / multi-label
  sheet_label_height: number;
  labels_per_row: number;
  sheet_labels_count: number;
  margin_top: number;
  margin_bottom: number;
  margin_left: number;
  margin_right: number;
  labels_horizontal_gap: number;

  // Driver passthrough settings
  printer_settings: AssetTagTemplatePrinterSettings;
}

export interface AssetTagTemplateListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AssetTagTemplate[];
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// Use type alias instead of empty interface extension to avoid ESLint error
export type AssetUpdateInput = Partial<AssetCreateInput>;

// Create API
export const assetApi = createApi({
  reducerPath: 'assetApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Asset', 'AssetCategory', 'AssetCondition', 'AssetStatus', 'AssetTagTemplate'],
  keepUnusedDataFor: 120,
  endpoints: (builder) => ({
    getAssets: builder.query<
      { count: number; limit: number; next: string | null; previous: string | null; results: Asset[] },
      GetAssetsParams
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        return {
          url: "assets/",
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'Asset' as const, id })),
              { type: 'Asset', id: 'LIST' },
            ]
          : [{ type: 'Asset', id: 'LIST' }],
    }),
    getAssetCategories: builder.query<
      PaginatedResponse<any>,
      Record<string, any>
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        return {
          url: 'assets/asset-categories/',
          method: 'GET',
          params: queryParams,
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'AssetCategory' as const, id })),
              { type: 'AssetCategory', id: 'LIST' },
            ]
          : [{ type: 'AssetCategory', id: 'LIST' }],
    }),

    // New dropdown-specific endpoint for asset categories
    getAssetCategoriesDropdown: builder.query<
      PaginatedResponse<{ id: string; name: string }>,
      {
        search?: string,
      }
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        if (params.search) {
          queryParams['search'] = params.search;
        }
        queryParams['paginate'] = "false";
        return {
          url: 'assets/asset-categories/dropdown/',
          method: 'GET',
          params: queryParams,

        }
      },
      providesTags: ['AssetCategory'],
    }),

    getAssetById: builder.query<Asset, string>({
      query: (id) => `assets/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Asset', id }],
    }),
    createAsset: builder.mutation<Asset, FormData>({
      query: (asset) => ({
        url: 'assets/',
        method: 'POST',
        body: asset,
      }),
      invalidatesTags: [{ type: 'Asset', id: 'LIST' }],
    }),
    updateAsset: builder.mutation<Asset, { id: string; asset: FormData }>({
      query: ({ id, asset }) => ({
        url: `assets/${id}/`,
        method: 'PATCH',
        body: asset,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Asset', id }, { type: 'Asset', id: 'LIST' }],
    }),
    addUpdateAsset: builder.mutation<Asset, { id: string; asset: AssetUpdateInput }>({
      query: ({ id, asset }) => ({
        url: `assets/${id}/`,
        method: 'PATCH',
        body: asset,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Asset', id }, { type: 'Asset', id: 'LIST' }],
    }),
    deleteAsset: builder.mutation<void, string>({
      query: (id) => ({
        url: `assets/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Asset', id: 'LIST' }],
    }),
    getAssetConditions: builder.query<
      PaginatedResponse<{ id: string; name: string }>,
      {
        search?: string,
        page?: number,
        limit?: number,
        dropdown?: boolean,
      }
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        if (params.dropdown) {
          queryParams['dropdown'] = 'true';
        }
        if (params.search) {
          queryParams['search'] = params.search;
        }
        if (params.page) {
          queryParams['page'] = params.page;
        }
        if (params.limit) {
          queryParams['limit'] = params.limit;
        }
        return {
          url: 'master/asset-conditions/',
          method: 'GET',
          params: queryParams,

        }
      },
      providesTags: ['AssetCondition'],
    }),

    // New dropdown-specific endpoint for asset conditions
    getAssetConditionsDropdown: builder.query<
      PaginatedResponse<{ id: string; name: string }>,
      {
        search?: string,
        page?: number,
        limit?: number,
        dropdown?: boolean,
      }
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        if (params.search) {
          queryParams['search'] = params.search;
        }
        if (params.page) {
          queryParams['page'] = params.page;
        }
        if (params.limit) {
          queryParams['limit'] = params.limit;
        }
        return {
          url: 'master/asset-conditions/dropdown/',
          method: 'GET',
          params: queryParams,

        }
      },
      providesTags: ['AssetCondition'],
    }),

    getAssetStatus: builder.query<
      PaginatedResponse<{ id: string; name: string }>,
      {
        search?: string,
        page?: number,
        limit?: number,
        dropdown?: boolean,
       }
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        if (params.dropdown) {
          queryParams['dropdown'] = 'true';
        }
        if (params.search) {
          queryParams['search'] = params.search;
        }
        if (params.page) {
          queryParams['page'] = params.page;
        }
        if (params.limit) {
          queryParams['limit'] = params.limit;
        }
        return {
          url: 'assets/addon/asset-statuses/',
          method: 'GET',
          params: queryParams,

        }
      },
      providesTags: ['AssetStatus'],
    }),

    // Dropdown for asset status. Backend has no dedicated /dropdown/ route (unlike asset conditions);
    // AssetStatusViewSet list supports ?dropdown=true and returns AssetStatusWebDropdownSerializer.
    getAssetStatusDropdown: builder.query<
      PaginatedResponse<{ id: string; name: string }>,
      {
        search?: string;
        page?: number;
        limit?: number;
        dropdown?: boolean;
      }
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        queryParams['dropdown'] = 'true';
        if (params.search) {
          queryParams['search'] = params.search;
        }
        if (params.page) {
          queryParams['page'] = params.page;
        }
        if (params.limit) {
          queryParams['limit'] = params.limit;
        }
        return {
          url: 'assets/addon/asset-statuses/',
          method: 'GET',
          params: queryParams,
        };
      },
      providesTags: ['AssetStatus'],
    }),

    // New dropdown-specific endpoint for assets
    getAssetsDropdown: builder.query<
      { count: number; limit: number; next: string | null; previous: string | null; results: Asset[] },
      GetAssetsParams
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};
        
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        return {
          url: "assets/dropdown/",
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: ["Asset"],
    }),

    // Same shape as getAssetsDropdown, but excludes assets already mid-transfer,
    // mid-disposal, or on an open movement-tracked activity - for the Schedule
    // and one-off Activity asset-selection pickers specifically.
    getSchedulableAssetsDropdown: builder.query<
      { count: number; limit: number; next: string | null; previous: string | null; results: Asset[] },
      GetAssetsParams
    >({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};

        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams[key] = typeof value === 'boolean' ? value.toString() : value;
          }
        });

        return {
          url: "schedule/schedulable-assets/",
          method: "GET",
          params: queryParams,
        };
      },
      providesTags: ["Asset"],
    }),

    getAssetTagTemplates: builder.query<
      AssetTagTemplateListResponse,
      Record<string, any>
    >({
      query: (params) => ({
        url: "asset-tag-template/",
        method: "GET",
        params,
      }),

      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "AssetTagTemplate" as const,
                id,
              })),
              { type: "AssetTagTemplate", id: "LIST" },
            ]
          : [{ type: "AssetTagTemplate", id: "LIST" }],
    }),

    getAssetTagTemplate: builder.query<
      AssetTagTemplate,
      string
    >({
      query: (id) => ({
        url: `asset-tag-template/${id}/`,
        method: "GET",
      }),

      providesTags: (_result, _error, id) => [
        {
          type: "AssetTagTemplate",
          id,
        },
      ],
    }),

    createAssetTagTemplate: builder.mutation<
      AssetTagTemplate,
      FormData
    >({
      query: (body) => ({
        url: "asset-tag-template/",
        method: "POST",
        body,
      }),

      invalidatesTags: [
        {
          type: "AssetTagTemplate",
          id: "LIST",
        },
      ],
    }),

    updateAssetTagTemplate: builder.mutation<
      AssetTagTemplate,
      {
        id: string;
        body: FormData;
      }
    >({
      query: ({ id, body }) => ({
        url: `asset-tag-template/${id}/`,
        method: "PATCH",
        body,
      }),

      invalidatesTags: (_r, _e, { id }) => [
        {
          type: "AssetTagTemplate",
          id,
        },
        {
          type: "AssetTagTemplate",
          id: "LIST",
        },
      ],
    }),

    deleteAssetTagTemplate: builder.mutation<
      void,
      string
    >({
      query: (id) => ({
        url: `asset-tag-template/${id}/`,
        method: "DELETE",
      }),

      invalidatesTags: [
        {
          type: "AssetTagTemplate",
          id: "LIST",
        },
      ],
    }),

    deleteAssetCategory: builder.mutation<void, string>({
      query: (id) => ({
        url: `assets/asset-categories/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'AssetCategory', id: 'LIST' }],
    }),
  }),
});

// Export hooks
export const {
  useGetAssetsQuery,
  useGetAssetsDropdownQuery,
  useGetSchedulableAssetsDropdownQuery,
  useGetAssetCategoriesQuery,
  useGetAssetCategoriesDropdownQuery,
  useGetAssetByIdQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useAddUpdateAssetMutation,
  useDeleteAssetMutation,
  useDeleteAssetCategoryMutation,
  useGetAssetConditionsQuery,
  useGetAssetConditionsDropdownQuery,
  useGetAssetStatusQuery,
  useGetAssetStatusDropdownQuery,
  useGetAssetTagTemplatesQuery,
  useGetAssetTagTemplateQuery,
  useCreateAssetTagTemplateMutation,
  useUpdateAssetTagTemplateMutation,
  useDeleteAssetTagTemplateMutation,
} = assetApi;